<template>
  <VDialog
    :value="value"
    persistent
    eager
    max-width="700"
    @input="$emit('input', $event)"
  >
    <VCard class="onboarding-modal">
      <!-- Header -->
      <VCardTitle class="d-flex justify-space-between align-center">
        <span>Complete Account Setup</span>
        <VBtn
          icon
          @click="handleClose"
        >
          <VIcon color="var(--color-blue-800)">{{ mdiClose }}</VIcon>
        </VBtn>
      </VCardTitle>

      <VDivider />

      <!-- Stripe Connect Container (always rendered so the DOM node is stable) -->
      <VCardText class="pa-0">
        <div
          v-if="isLoading"
          class="text-center py-12"
        >
          <VProgressCircular
            indeterminate
            color="primary"
            size="48"
          />
          <div class="mt-4 text--secondary">Loading Stripe...</div>
        </div>
        <div style="padding: 16px">
          <div
            ref="stripeContainer"
            :style="{ display: isLoading ? 'none' : 'block' }"
          />
        </div>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount } from 'vue'
import { ConnectHTMLElementRecord, loadConnectAndInitialize } from '@stripe/connect-js'
import type { StripeConnectInstance } from '@stripe/connect-js'
import { mdiClose } from '@mdi/js'

const STRIPE_PUBLISHABLE_KEY = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY!

interface Emits {
  (e: 'input', value: boolean): void
  (e: 'complete'): void
}

const props = defineProps({
  value: {
    type: Boolean,
    required: true,
  },
  clientSecret: {
    type: String,
    default: null,
  },
})
const emit = defineEmits<Emits>()

const isLoading = ref(false)
const stripeContainer = ref<HTMLElement | null>(null)
let stripeInstance: StripeConnectInstance | null = null
let onboardingComponent: ConnectHTMLElementRecord['account-onboarding'] | null = null

const handleOnboardingComplete = () => {
  emit('complete')
  emit('input', false)
}

const handleClose = () => {
  emit('input', false)
}

const cleanup = () => {
  if (onboardingComponent) {
    onboardingComponent.remove()
    onboardingComponent = null
  }
  stripeInstance = null
}

const initializeStripe = async (clientSecret: string) => {
  cleanup()
  isLoading.value = true

  try {
    stripeInstance = loadConnectAndInitialize({
      publishableKey: STRIPE_PUBLISHABLE_KEY,
      fetchClientSecret: async () => clientSecret,
      appearance: {
        overlays: 'dialog',
        variables: {
          colorPrimary: '#6366f1',
          colorBackground: '#ffffff',
          fontFamily: 'Roboto, sans-serif',
          borderRadius: '8px',
        },
      },
    })

    isLoading.value = false

    if (stripeContainer.value) {
      onboardingComponent = stripeInstance.create('account-onboarding')
      onboardingComponent.setOnExit(handleOnboardingComplete)
      stripeContainer.value.appendChild(onboardingComponent)
    }
  } catch (error) {
    console.error('Failed to initialize Stripe Connect:', error)
    isLoading.value = false
  }
}

// Initialize Stripe when modal opens with clientSecret
watch(
  () => props.value,
  async (isOpen) => {
    if (isOpen && props.clientSecret) {
      await initializeStripe(props.clientSecret)
    } else if (!isOpen) {
      cleanup()
    }
  }
)

// Cleanup on unmount
onBeforeUnmount(cleanup)
</script>

<style scoped lang="scss">
.onboarding-modal {
  border-radius: 12px;
  overflow: hidden;
}

.stripe-container {
  min-height: 500px;
  padding: 20px;
}
</style>
